import { useMemo } from "react";
import { useInView } from "react-intersection-observer";
import {
  useExpiringClients,
  type ExpiringClient,
} from "../../../../hooks/subscription/sidebar/useExpiringClients";
import { useAccessExceptions } from "../../../../hooks/accessControl/useAccessExceptions";
import type { AccessExceptionEntry } from "../../../../hooks/requests/accessControl";
import { useModuleEnabled } from "../../../../hooks/gym/useModuleEnabled";
import { useAuth } from "../../../../contexts/auth/Context";
import { decodeUserInfo } from "../../../../utils/user";
import { useSidebarToggle } from "../../../../contexts/sidebar/useSidebarToggle";
import { getClientDisplayName } from "../../../../utils/client";

const ClientItem = ({
  name,
  plan,
  statusText,
  isUrgent,
}: {
  name: string;
  plan: string;
  statusText: string;
  isUrgent: boolean;
}) => (
  <div className="flex items-center justify-between gap-2 py-1.5 border-b border-global-secondary last:border-0">
    <div className="min-w-0 flex-1">
      <p className="text-size-2xs font-medium text-global-primary truncate">
        {name}
      </p>
      <p className="text-size-2xs text-global-tertiary truncate">{plan}</p>
    </div>
    <span
      className={`px-1.5 py-0.5 rounded text-size-2xs font-semibold shrink-0 ${
        isUrgent
          ? "bg-(--color-error-bg) text-(--color-error-text)"
          : "bg-(--metric-average-bg) text-(--metric-average)"
      }`}
    >
      {statusText}
    </span>
  </div>
);

const MAX_REASON_LENGTH = 40;
const formatExceptionTime = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  const isToday =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  if (isToday) {
    return d.toLocaleTimeString("es-CL", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }
  return d.toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const ExceptionItem = ({ entry }: { entry: AccessExceptionEntry }) => {
  const reasonText =
    entry.reason.length > MAX_REASON_LENGTH
      ? `${entry.reason.slice(0, MAX_REASON_LENGTH)}…`
      : entry.reason;
  return (
    <div className="py-1.5 border-b border-global-secondary last:border-0 space-y-0.5">
      <p className="text-size-2xs font-medium text-global-primary truncate">
        {entry.clientName}
      </p>
      <p
        className="text-size-2xs text-global-tertiary line-clamp-2"
        title={entry.reason}
      >
        {reasonText}
      </p>
      <p className="text-size-2xs text-global-muted truncate">
        Por {entry.authorizedBy} · {formatExceptionTime(entry.createdAt)}
      </p>
    </div>
  );
};

const ListSkeleton = () => (
  <div className="space-y-1 animate-pulse">
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className="h-12 bg-global-overlay-secondary rounded-md border border-global-secondary"
      />
    ))}
  </div>
);

const SubscriptionSection = ({
  title,
  count,
  colorClass,
  bgClass,
  loading,
  data,
  infiniteProps,
  renderItem,
}: {
  title: string;
  count: number;
  colorClass: string;
  bgClass: string;
  loading: boolean;
  data: ExpiringClient[];
  infiniteProps: {
    onLoadMore: () => void;
    hasMore: boolean;
    isFetching: boolean;
  };
  renderItem: (c: ExpiringClient) => React.ReactNode;
}) => {
  const { ref } = useInView({
    threshold: 0,
    onChange: (inView) =>
      inView &&
      infiniteProps.hasMore &&
      !infiniteProps.isFetching &&
      infiniteProps.onLoadMore(),
  });

  if (loading)
    return (
      <div className="space-y-1.5">
        <h4 className={`text-size-2xs font-semibold uppercase ${colorClass}`}>
          {title}
        </h4>
        <ListSkeleton />
      </div>
    );

  if (data.length === 0 && !infiniteProps.hasMore) return null;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between px-0.5">
        <h4
          className={`text-size-2xs font-semibold uppercase tracking-wide ${colorClass}`}
        >
          {title}
        </h4>
        <span
          className={`px-1.5 py-0.5 rounded text-size-2xs font-bold ${bgClass} ${colorClass}`}
        >
          {count}
        </span>
      </div>
      <div className="max-h-40 overflow-y-auto custom-scrollbar">
        {data.map(renderItem)}
        <div ref={ref} className="h-1 flex justify-center py-1">
          {infiniteProps.isFetching && (
            <span className="text-size-2xs text-global-tertiary">...</span>
          )}
        </div>
      </div>
    </div>
  );
};

interface RightSidebarProps {
  variant?: "desktop" | "drawer";
  onClose?: () => void;
}

export const RightSidebar = ({
  variant = "desktop",
  onClose,
}: RightSidebarProps) => {
  const planSalesEnabled = useModuleEnabled("planSales");
  const accessControlEnabled = useModuleEnabled("accessControl");

  const clients = useExpiringClients({ enabled: planSalesEnabled });
  const exceptions = useAccessExceptions(10, { enabled: accessControlEnabled });
  const { token } = useAuth();
  const { gymName } = useMemo(() => decodeUserInfo(token), [token]);
  const { isRightCollapsed } = useSidebarToggle();

  const isDrawer = variant === "drawer";

  const isEmpty =
    (planSalesEnabled
      ? !clients.isLoading &&
        !clients.isFetchingNextPage &&
        clients.expiringSoon.length === 0 &&
        clients.longExpired.length === 0
      : true) &&
    (accessControlEnabled
      ? !exceptions.isLoading && (exceptions.data?.exceptions.length ?? 0) === 0
      : true);

  const content = (
    <>
      <header className="p-2.5 flex items-center gap-2 border-b border-global-secondary backdrop-blur-sm bg-global-primary/95">
        <div className="w-7 h-7 bg-linear-to-br from-global-accent to-global-accent/70 rounded-lg flex items-center justify-center shadow-sm">
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-size-xs font-bold text-global-primary truncate">
            {gymName?.toUpperCase() || "No disponible"}
          </p>
          <p className="text-size-2xs text-global-tertiary">Negocio</p>
        </div>
        {isDrawer && onClose && (
          <button
            type="button"
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center rounded-md text-global-tertiary hover:text-global-primary hover:bg-global-overlay-secondary transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </header>

      <div className="flex-1 overflow-y-auto p-2.5 space-y-4">
        {planSalesEnabled && (
          <>
            <SubscriptionSection
              title="Próximos a expirar"
              count={clients.expiringSoon.length}
              colorClass="metric-average-value"
              bgClass="bg-(--metric-average-bg)"
              loading={clients.isLoading}
              data={clients.expiringSoon}
              infiniteProps={{
                onLoadMore: clients.expiringSoonQuery.fetchNextPage,
                hasMore: clients.expiringSoonQuery.hasNextPage ?? false,
                isFetching: clients.isFetchingNextPage,
              }}
              renderItem={(c: ExpiringClient) => (
                <ClientItem
                  key={c.publicId}
                  name={getClientDisplayName(c)}
                  plan={c.planName}
                  isUrgent={false}
                  statusText={
                    c.daysRemaining === 0 ? "Expira hoy" : `${c.daysRemaining}d`
                  }
                />
              )}
            />

            <SubscriptionSection
              title="Expirados recientemente"
              count={clients.longExpired.length}
              colorClass="text-global-error"
              bgClass="bg-(--color-error-bg)"
              loading={clients.isLoading}
              data={clients.longExpired}
              infiniteProps={{
                onLoadMore: clients.longExpiredQuery.fetchNextPage,
                hasMore: clients.longExpiredQuery.hasNextPage ?? false,
                isFetching: clients.isFetchingNextPage,
              }}
              renderItem={(c: ExpiringClient) => (
                <ClientItem
                  key={c.publicId}
                  name={getClientDisplayName(c)}
                  plan={c.planName}
                  isUrgent={true}
                  statusText={
                    c.daysExpired === 7
                      ? "Expirados hace 7 días"
                      : `${c.daysExpired}d`
                  }
                />
              )}
            />
          </>
        )}

        {accessControlEnabled &&
          (exceptions.isLoading ? (
            <div className="space-y-1.5">
              <h4 className="text-size-2xs font-semibold uppercase text-global-warning">
                Ingresos excepcionales
              </h4>
              <ListSkeleton />
            </div>
          ) : exceptions.data?.exceptions &&
            exceptions.data.exceptions.length > 0 ? (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between px-0.5">
                <h4 className="text-size-2xs font-semibold uppercase tracking-wide text-global-warning">
                  Ingresos excepcionales
                </h4>
                <span className="px-1.5 py-0.5 rounded text-size-2xs font-bold bg-global-warning/20 text-global-warning">
                  {exceptions.data.exceptions.length}
                </span>
              </div>
              <div className="max-h-40 overflow-y-auto custom-scrollbar">
                {exceptions.data.exceptions.map((entry) => (
                  <ExceptionItem key={entry.publicId} entry={entry} />
                ))}
              </div>
            </div>
          ) : null)}

        {isEmpty && (
          <div className="p-3 text-center bg-global-overlay-secondary rounded-md border border-global-secondary text-size-2xs text-global-tertiary">
            No hay novedades por mostrar
          </div>
        )}
      </div>

      <footer className="px-2.5 py-2 border-t border-global-secondary flex items-center justify-between text-size-2xs">
        <div className="flex items-center gap-1.5 text-global-tertiary">
          <div className="w-1.5 h-1.5 bg-(--metric-sales) rounded-full" />
          Estado
        </div>
        <span className="text-global-secondary font-medium">v1.0.0</span>
      </footer>
    </>
  );

  if (isDrawer) {
    return (
      <aside className="flex flex-col h-full bg-global-primary overflow-hidden">
        {content}
      </aside>
    );
  }

  return (
    <div
      className="hidden xl:block shrink-0 sticky top-[calc(var(--height-navbar)+var(--height-env-banner))] right-0 z-30 h-[calc(100vh-var(--height-navbar)-var(--height-env-banner))] overflow-hidden transition-[width] duration-300 ease-in-out"
      style={{ width: isRightCollapsed ? 0 : "var(--width-sidebar-right)" }}
    >
      <aside className="flex flex-col h-full bg-global-primary border-l border-global-secondary overflow-hidden sidebar-right-width">
        {content}
      </aside>
    </div>
  );
};
